import React, { useState, useRef, useEffect } from 'react';
import { queryAPI, exportAPI, chatAPI } from '../services/api';
import AnalyticsDashboard from '../components/AnalyticsDashboard';
import SmartDashboard from '../components/SmartDashboard';
import ContactCard from '../components/ContactCard';
import OnboardingTour, { USER_TOUR_STEPS, ADMIN_EXTRA_STEPS } from '../components/OnboardingTour';
import { useAuth } from '../hooks/useAuth';
import { useLocation, useNavigate } from 'react-router-dom';

const ANALYTICS_TABLES = new Set(['OSQ', 'OSINV', 'OSO', 'OPOR', 'SQ1', 'SINV1', 'SO1', 'POR1']);

const FALLBACK_SUGGESTIONS = [
  'Show all open sales quotations for last month',
  'Top 10 customers by total quotation value',
  'Monthly quotation trend this financial year',
  'All won quotations above ₹1,00,000',
  'Show total revenue last FY',
  'Sales executive wise quotation summary'
];

const HERO_CARDS = [
  { icon: '📊', title: 'Sales Quotations',  desc: 'Open pipeline, values, customer-wise breakdown', query: 'Show all open sales quotations for last month' },
  { icon: '🧾', title: 'Invoice Revenue',   desc: 'Billed revenue, top customers, FY comparisons',  query: 'Total invoice revenue by customer this financial year' },
  { icon: '📦', title: 'Purchase Orders',   desc: 'Vendor spend, pending POs, approvals',            query: 'Top 10 vendors by purchase order value this year' },
  { icon: '🏆', title: 'Top Performers',    desc: 'Sales executive wise performance summary',         query: 'Sales executive wise quotation summary for last month' },
];

function timeGreeting() {
  const h = new Date().getHours();
  return h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening';
}

function createSession(id) {
  return { id, name: '', messages: [], conversationHistory: [],
           lastQueryTable: 'OSQ', lastQueryText: '', lastQueryResult: null, inputExpanded: false };
}

export default function Home() {
  const [query,               setQuery]               = useState('');
  const [loading,             setLoading]             = useState(false);
  const [error,               setError]               = useState(null);
  const [saveDialog,          setSaveDialog]          = useState(false);
  const [saveTarget,          setSaveTarget]          = useState(null);
  const [saveName,            setSaveName]            = useState('');
  const [pageIn,              setPageIn]              = useState(false);
  const [suggestions,         setSuggestions]         = useState(FALLBACK_SUGGESTIONS);
  const [showDashDropdown,    setShowDashDropdown]    = useState(false);
  const [analyticsLoading,    setAnalyticsLoading]    = useState(false);
  const [sessions,            setSessions]            = useState(() => [createSession('s_init')]);
  const [activeSessionId,     setActiveSessionId]     = useState('s_init');

  const { user } = useAuth();
  const [showTour, setShowTour] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const textareaRef = useRef(null);
  const bottomRef   = useRef(null);
  const chatAreaRef = useRef(null);
  const inputBarRef = useRef(null);

  const activeSession = sessions.find(s => s.id === activeSessionId) ?? sessions[0];
  const { messages, conversationHistory, lastQueryTable, lastQueryText, lastQueryResult, inputExpanded } = activeSession;
  const patchActive = (fn) =>
    setSessions(prev => prev.map(s => s.id === activeSessionId ? { ...s, ...fn(s) } : s));

  useEffect(() => {
    const t = setTimeout(() => setPageIn(true), 80);

    chatAPI.getHistory()
      .then(r => {
        if (r.data.messages?.length > 0) {
          const restored = r.data.messages;
          const lastWithRows = [...restored].reverse().find(m => m.result?.rows?.length > 0 && !m.result?.needs_clarification);
          setSessions(prev => prev.map((s, i) => i !== 0 ? s : {
            ...s,
            messages: restored,
            name: restored[0]?.query?.slice(0, 28) || '',
            ...(lastWithRows ? {
              lastQueryResult: lastWithRows.result,
              lastQueryText: lastWithRows.query || '',
              lastQueryTable: lastWithRows.result?.meta?.injected_tables?.[0] || s.lastQueryTable,
            } : {})
          }));
        }
      })
      .catch(() => {});

    // Fire onboarding tour once per user (keyed by user id in localStorage)
    if (user?.id && !localStorage.getItem(`tour_done_${user.id}`)) {
      const tt = setTimeout(() => setShowTour(true), 900);
      return () => { clearTimeout(t); clearTimeout(tt); };
    }

    queryAPI.suggestions()
      .then(r => { if (r.data.suggestions?.length >= 3) setSuggestions(r.data.suggestions); })
      .catch(() => {});

    return () => clearTimeout(t);
  }, []);

  // Auto-run a query coming from the History page "Run" button
  useEffect(() => {
    const q = location.state?.runQuery;
    if (q) {
      navigate('/', { replace: true, state: null });
      setTimeout(() => handleSubmit(q), 120);
    }
  }, [location.state?.runQuery]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [query]);

  // Auto-scroll to bottom whenever messages or loading changes
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  // Expand the input bar whenever the chat is active
  useEffect(() => {
    if (messages.length > 0 || loading) patchActive(_ => ({ inputExpanded: true }));
  }, [messages.length, loading]);

  // Collapse back to pill when clicking outside, only if bar is empty and no messages
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        inputExpanded &&
        !query.trim() &&
        messages.length === 0 &&
        inputBarRef.current &&
        !inputBarRef.current.contains(e.target)
      ) {
        patchActive(_ => ({ inputExpanded: false }));
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [inputExpanded, query, messages.length]);

  const completeTour = () => {
    if (user?.id) localStorage.setItem(`tour_done_${user.id}`, '1');
    setShowTour(false);
  };

  const handleSubmit = async (q) => {
    const text = (q || query).trim();
    if (!text) return;
    setQuery('');
    setLoading(true);
    setError(null);
    try {
      const historyToSend = isFollowUpQuery(text, conversationHistory.length > 0)
        ? conversationHistory
        : [];

      const res = await queryAPI.run(text, historyToSend);
      const newMsg = { id: Date.now(), query: text, result: res.data };
      const clarification = res.data?.needs_clarification;
      const primaryTable = res.data?.meta?.injected_tables?.[0];

      patchActive(s => ({
        messages: [...s.messages, newMsg],
        name: s.name || (text.length > 28 ? text.slice(0, 28) + '…' : text),
        ...(res.data?.rows && !clarification ? { lastQueryResult: res.data } : {}),
        ...(!clarification && primaryTable ? { lastQueryTable: primaryTable, lastQueryText: text } : {}),
      }));

      if (clarification) return;

      let sqlForHistory = res.data?.sql;
      if (res.data?.type === 'analytics' && res.data?.sqlQueries?.kpis) {
        sqlForHistory = res.data.sqlQueries.kpis;
      }
      if (sqlForHistory) {
        patchActive(s => ({
          conversationHistory: [...s.conversationHistory.slice(-4), { query: text, sql: sqlForHistory }]
        }));
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
  };

  const handleClearChat = async () => {
    try { await chatAPI.clearHistory(); } catch (_) {}
    patchActive(_ => ({ messages: [], lastQueryResult: null, conversationHistory: [], inputExpanded: false }));
    setError(null);
    queryAPI.suggestions()
      .then(r => { if (r.data.suggestions?.length >= 3) setSuggestions(r.data.suggestions); })
      .catch(() => {});
  };

  const handleAnalytics = () => {
    if (!lastQueryResult) return;
    patchActive(s => ({ messages: [...s.messages, {
      id: Date.now(),
      query: `Dashboard: ${lastQueryText}`,
      result: { ...lastQueryResult, type: 'smart_dashboard', queryText: lastQueryText, tableName: lastQueryTable }
    }]}));
  };

  const handleFullAnalytics = async () => {
    setAnalyticsLoading(true);
    setError(null);
    try {
      const res = await queryAPI.analytics(lastQueryTable, lastQueryText);
      patchActive(s => ({ messages: [...s.messages, { id: Date.now(), query: `${lastQueryTable} Dashboard`, result: res.data }] }));
    } catch (err) {
      setError('Failed to load analytics dashboard.');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!saveName.trim() || !saveTarget) return;
    try {
      await queryAPI.save(saveName, saveTarget.query, saveTarget.result.sql);
      setSaveDialog(false); setSaveName(''); setSaveTarget(null);
    } catch { alert('Failed to save query.'); }
  };

  const openSaveDialog = (msg) => {
    setSaveTarget({ query: msg.query, result: msg.result });
    setSaveDialog(true);
  };

  const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = async (msg) => {
    if (!msg.result?.rows?.length) return;
    try {
      const res = await exportAPI.pdf({ rows: msg.result.rows, query: msg.query, sql: msg.result.sql, timestamp: new Date().toISOString() });
      triggerDownload(new Blob([res.data], { type: 'application/pdf' }), `erp-report-${Date.now()}.pdf`);
    } catch { alert('PDF export failed.'); }
  };

  const exportExcel = async (msg) => {
    if (!msg.result?.rows?.length) return;
    try {
      const res = await exportAPI.excel({ rows: msg.result.rows, query: msg.query, sql: msg.result.sql, timestamp: new Date().toISOString() });
      triggerDownload(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `erp-report-${Date.now()}.xlsx`);
    } catch { alert('Excel export failed.'); }
  };

  const addTab = () => {
    const id = `s_${Date.now()}`;
    setSessions(prev => [...prev, createSession(id)]);
    setActiveSessionId(id);
    setQuery('');
    setError(null);
  };

  const closeTab = (id) => {
    setSessions(prev => {
      const rest = prev.filter(s => s.id !== id);
      if (rest.length === 0) return prev;
      if (activeSessionId === id) {
        const idx = prev.findIndex(s => s.id === id);
        setActiveSessionId(rest[Math.min(idx, rest.length - 1)].id);
      }
      return rest;
    });
  };

  const switchTab = (id) => {
    setActiveSessionId(id);
    setQuery('');
    setError(null);
    setShowDashDropdown(false);
  };

  return (
    <div style={{
      ...S.page,
      opacity: pageIn ? 1 : 0,
      transition: 'opacity 0.45s ease'
    }}>

      {/* ── Header ── */}
      <div style={S.header}>
        <h1 style={S.title} className="gradient-text">Ask your data anything</h1>
        <p style={S.subtitle}>Natural language → SQL → instant results from your ERP</p>
      </div>

      {/* ── Tab strip ── */}
      <div style={S.tabStrip}>
        {sessions.map(session => (
          <div
            key={session.id}
            style={{ ...S.tab, ...(session.id === activeSessionId ? S.tabActive : {}) }}
            onClick={() => switchTab(session.id)}
          >
            <span style={S.tabName}>{session.name || 'New Chat'}</span>
            {sessions.length > 1 && (
              <button
                onClick={e => { e.stopPropagation(); closeTab(session.id); }}
                style={S.tabClose}
              >×</button>
            )}
          </div>
        ))}
        <button onClick={addTab} style={S.tabAdd} title="New tab">+</button>
      </div>

      {/* ── Chat Area ── */}
      <div style={S.chatArea} ref={chatAreaRef}>

        {/* Hero welcome state (only when no messages yet) */}
        {messages.length === 0 && !loading && (
          <div id="tour-suggestions" style={S.hero}>
            {/* Greeting */}
            <div style={S.heroGreeting} className="gradient-text">
              Good {timeGreeting()}, {user?.name?.split(' ')[0] || 'there'} 👋
            </div>
            <div style={S.heroTagline}>What would you like to know today?</div>

            {/* 2×2 suggestion cards */}
            <div style={S.heroGrid}>
              {HERO_CARDS.map((card, i) => (
                <button
                  key={card.title}
                  onClick={() => handleSubmit(card.query)}
                  className="hero-card"
                  style={{ ...S.heroCard, animation: `fadeInUp 0.4s ease ${0.08 + i * 0.09}s both` }}
                >
                  <div style={S.heroCardIcon}>{card.icon}</div>
                  <div style={S.heroCardTitle}>{card.title}</div>
                  <div style={S.heroCardDesc}>{card.desc}</div>
                  <div style={S.heroCardArrow}>→</div>
                </button>
              ))}
            </div>

            {/* Extra smaller suggestions */}
            <div style={S.moreRow}>
              <span style={S.moreLabel}>More ideas</span>
              {suggestions.slice(0, 4).map((s, i) => (
                <button key={s} onClick={() => handleSubmit(s)} className="chip-hover"
                  style={{ ...S.chip, animation: `fadeInUp 0.3s ease ${0.45 + i * 0.05}s both` }}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Past + current messages */}
        {messages.map((msg, idx) => (
          <ChatTurn
            key={msg.id}
            msg={msg}
            turnIndex={idx + 1}
            onSave={() => openSaveDialog(msg)}
            onExportPDF={() => exportPDF(msg)}
            onExportExcel={() => exportExcel(msg)}
            onClarify={(opt) => handleSubmit(opt)}
          />
        ))}

        {/* Loading indicator */}
        {loading && (
          <div style={{ ...S.loadingBox, animation: 'fadeIn 0.3s ease both' }}>
            <div style={S.loadingRing}>
              <div style={S.ring1} />
              <div style={S.ring2} />
              <img src="/logo.svg" alt="" style={{ width: 36, height: 'auto', objectFit: 'contain', filter: 'drop-shadow(0 0 6px rgba(27,94,59,0.35))' }} />
            </div>
            <div>
              <div style={S.loadingTitle}>Processing your query</div>
              <div style={S.loadingSteps}>
                {['Classifying intent', 'Resolving schema', 'Generating SQL', 'Executing…'].map((step, i) => (
                  <span key={step} style={{ ...S.loadingStep, animation: `fadeIn 0.3s ease ${i * 0.4}s both` }}>
                    {step}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ ...S.errorBox, animation: 'fadeInUp 0.3s ease both' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            {error}
          </div>
        )}

        {/* Scroll anchor */}
        <div ref={bottomRef} />
      </div>

      {/* ── Input Bar (pinned bottom) ── */}
      <div style={S.inputBar} ref={inputBarRef}>

        {/* ── Collapsed pill (empty home screen) ── */}
        {!inputExpanded && (
          <div
            className="pill-bar"
            onClick={() => { patchActive(_ => ({ inputExpanded: true })); setTimeout(() => textareaRef.current?.focus(), 320); }}
            style={S.pillBar}
          >
            <div style={S.pillIcon}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1B5E3B" strokeWidth="2">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
              </svg>
            </div>
            <span style={S.pillPlaceholder}>Ask anything about your data…</span>
            <div style={S.pillSendCircle}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5">
                <line x1="22" y1="2" x2="11" y2="13"/>
                <polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </div>
          </div>
        )}

        {/* ── Expanded full bar ── */}
        {inputExpanded && (
          <>
            {messages.length > 0 && (
              <div style={{ ...S.contextHint, animation: 'fadeIn 0.2s ease both' }}>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#4CAF7D" strokeWidth="2">
                  <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                </svg>
                {messages.length} turn{messages.length > 1 ? 's' : ''} in context · follow-up questions understood
                <button onClick={handleClearChat} style={S.clearBtn} title="Clear this chat">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6"/>
                    <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                    <path d="M10 11v6M14 11v6"/>
                    <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                  </svg>
                  Clear chat
                </button>
              </div>
            )}
            <div className="query-box" style={{ ...S.queryBox, animation: 'expandBar 0.38s cubic-bezier(0.34, 1.56, 0.64, 1) both' }}>
              <textarea
                id="tour-input"
                ref={textareaRef}
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={messages.length > 0
                  ? 'Ask a follow-up or a new question…'
                  : 'e.g. Show all open quotations above ₹5,00,000 for last month'}
                style={S.textarea}
                rows={1}
                disabled={loading}
              />
              <div style={S.queryFooter}>
                <div style={{ position: 'relative' }}>
                  <button
                    id="tour-dashboard-btn"
                    onClick={() => setShowDashDropdown(o => !o)}
                    disabled={loading || !ANALYTICS_TABLES.has(lastQueryTable)}
                    className="btn-lift"
                    style={{ ...S.analyticsBtn, opacity: (loading || !ANALYTICS_TABLES.has(lastQueryTable)) ? 0.4 : 1 }}
                    title={!ANALYTICS_TABLES.has(lastQueryTable) ? `Dashboard not available for ${lastQueryTable}` : `View ${lastQueryTable} dashboard`}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
                    </svg>
                    Dashboard
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ marginLeft: 2, transition: 'transform 0.15s', transform: showDashDropdown ? 'rotate(180deg)' : 'rotate(0)' }}>
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </button>

                  {showDashDropdown && (
                    <>
                      <div style={{ position: 'fixed', inset: 0, zIndex: 49 }} onClick={() => setShowDashDropdown(false)} />
                      <div style={{ position: 'absolute', bottom: 'calc(100% + 6px)', left: 0, background: '#fff', border: '1.5px solid #cce8d8', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.13)', zIndex: 50, minWidth: 215, overflow: 'hidden', animation: 'fadeInUp 0.15s ease both' }}>
                        <button
                          onClick={() => { handleAnalytics(); setShowDashDropdown(false); }}
                          disabled={!lastQueryResult}
                          style={{ width: '100%', display: 'flex', flexDirection: 'column', padding: '11px 14px', background: 'none', border: 'none', borderBottom: '1px solid #e8f6ee', cursor: lastQueryResult ? 'pointer' : 'not-allowed', textAlign: 'left', opacity: lastQueryResult ? 1 : 0.45 }}
                        >
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>Smart Dashboard</span>
                          <span style={{ fontSize: 11, color: '#9b9b9b', marginTop: 2 }}>Instant · from your query rows</span>
                        </button>
                        <button
                          onClick={() => { if (!analyticsLoading) { handleFullAnalytics(); setShowDashDropdown(false); } }}
                          disabled={analyticsLoading}
                          style={{ width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', background: 'none', border: 'none', cursor: analyticsLoading ? 'not-allowed' : 'pointer', textAlign: 'left', opacity: analyticsLoading ? 0.6 : 1 }}
                        >
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a1a' }}>Full Analytics</span>
                            <span style={{ fontSize: 11, color: '#9b9b9b', marginTop: 2 }}>{analyticsLoading ? 'Loading…' : 'Pre-computed · rankings, trend, export'}</span>
                          </div>
                          {analyticsLoading && <span style={{ width: 13, height: 13, borderRadius: '50%', border: '2px solid rgba(27,94,59,0.2)', borderTopColor: '#1B5E3B', display: 'inline-block', animation: 'spin 0.7s linear infinite', flexShrink: 0 }} />}
                        </button>
                      </div>
                    </>
                  )}
                </div>
                <span style={S.enterHint}>
                  <kbd style={S.kbd}>Enter</kbd> to send &nbsp;·&nbsp; <kbd style={S.kbd}>Shift+Enter</kbd> for newline
                </span>
                <button
                  id="tour-send-btn"
                  onClick={() => handleSubmit()}
                  disabled={!query.trim() || loading}
                  className="btn-lift"
                  style={{ ...S.sendBtn, opacity: (!query.trim() || loading) ? 0.6 : 1 }}
                >
                  {loading ? <span style={S.spinner} /> : (
                    <>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="22" y1="2" x2="11" y2="13"/>
                        <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                      </svg>
                      Send
                    </>
                  )}
                </button>
              </div>
            </div>
          </>
        )}

      </div>

      {/* ── Onboarding tour ── */}
      {showTour && (
        <OnboardingTour
          steps={user?.role === 'admin' ? [...USER_TOUR_STEPS, ...ADMIN_EXTRA_STEPS] : USER_TOUR_STEPS}
          onComplete={completeTour}
        />
      )}

      {/* ── Save dialog ── */}
      {saveDialog && (
        <div style={S.overlay} onClick={() => setSaveDialog(false)}>
          <div style={{ ...S.dialog, animation: 'slideUpCard 0.3s ease both' }} onClick={e => e.stopPropagation()}>
            <h3 style={S.dialogTitle}>Save Query</h3>
            <p style={{ fontSize: 13, color: '#6b6b6b', marginBottom: 16 }}>Give this query a name for quick access later.</p>
            <input
              value={saveName}
              onChange={e => setSaveName(e.target.value)}
              placeholder="e.g. Monthly Q1 Quotations"
              className="fancy-input"
              style={S.dialogInput}
              onKeyDown={e => e.key === 'Enter' && handleSave()}
              autoFocus
            />
            <div style={S.dialogActions}>
              <button onClick={() => setSaveDialog(false)} style={S.cancelBtn}>Cancel</button>
              <button onClick={handleSave} style={S.saveDialogBtn} disabled={!saveName.trim()} className="btn-lift">
                Save Query
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .hero-card:hover {
          transform: translateY(-4px) !important;
          box-shadow: 0 12px 32px rgba(27,94,59,0.16) !important;
          border-color: #4CAF7D !important;
        }
        .hero-card:hover .hero-arrow { color: #1B5E3B !important; transform: translateX(3px); }

        .pill-bar:hover {
          border-color: #4CAF7D !important;
          box-shadow: 0 6px 28px rgba(27,94,59,0.20) !important;
          transform: translateY(-2px);
          animation: none !important;
        }

        @keyframes pillPulse {
          0%,100% { box-shadow: 0 4px 20px rgba(27,94,59,0.10); }
          50%      { box-shadow: 0 6px 30px rgba(27,94,59,0.22), 0 2px 8px rgba(27,94,59,0.10); }
        }

        @keyframes expandBar {
          from {
            transform: translateY(12px) scale(0.96);
            opacity: 0;
            border-radius: 50px;
          }
          to {
            transform: translateY(0) scale(1);
            opacity: 1;
            border-radius: 14px;
          }
        }
      `}</style>
    </div>
  );
}

// ── Chat Turn ─────────────────────────────────────────────────────────────

function ChatTurn({ msg, turnIndex, onSave, onExportPDF, onExportExcel, onClarify }) {
  const { query, result } = msg;
  const [sqlCopied, setSqlCopied] = useState(false);

  const copySQL = () => {
    if (result?.sql) {
      navigator.clipboard.writeText(result.sql);
      setSqlCopied(true);
      setTimeout(() => setSqlCopied(false), 1800);
    }
  };

  return (
    <div style={{ ...S.turn, animation: 'sectionReveal 0.4s ease both' }}>

      {/* User bubble */}
      <div style={S.userBubbleRow}>
        <div style={S.userBubble}>
          <div style={S.userBubbleLabel}>You</div>
          <div style={S.userBubbleText}>{query}</div>
        </div>
      </div>

      {/* Response */}
      <div style={S.responseArea}>

        {/* Chat response (greeting / irrelevant) */}
        {result?.type === 'chat' && (
          <div style={S.chatMsgCard}>
            <div style={S.chatMsgIcon}>🤖</div>
            <div style={S.chatMsgText}>
              {result.message.split('\n').map((line, i) => (
                <p key={i} style={{ margin: '3px 0', lineHeight: 1.65 }}>{line}</p>
              ))}
            </div>
          </div>
        )}

        {/* Clarification card */}
        {result?.needs_clarification && (
          <ClarificationCard
            question={result.clarification_question}
            options={result.clarification_options}
            onSelect={onClarify}
          />
        )}

        {/* Analytics dashboard */}
        {!result?.needs_clarification && result?.type === 'analytics' && (
          <AnalyticsDashboard data={result} />
        )}

        {/* Smart dashboard */}
        {!result?.needs_clarification && result?.type === 'smart_dashboard' && (
          <SmartDashboard
            rows={result.rows}
            queryText={result.queryText}
            tableName={result.tableName}
            sql={result.sql}
          />
        )}

        {/* Contact card */}
        {result?.type === 'contact' && (
          <ContactCard data={result} />
        )}

        {/* Sales recommendation */}
        {result?.type === 'recommendation' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ background: '#fff', border: '1.5px solid #C8EDD6', borderRadius: 14, overflow: 'hidden', boxShadow: '0 2px 12px rgba(27,94,59,0.08)' }}>
              <div style={{ background: 'linear-gradient(135deg, #0A2E17, #1B5E3B, #2E7D52)', padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 20 }}>🤝</span>
                <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>Sales Team Recommendation</span>
              </div>
              <div style={{ padding: '16px 18px', fontSize: 14, color: '#1a2e1a', lineHeight: 1.7 }}>
                {result.message}
              </div>
            </div>
            {result.salesData?.length > 0 && (
              <details style={{ background: '#f6fcf8', border: '1px solid #C8EDD6', borderRadius: 10, padding: '10px 14px' }}>
                <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#2E7D52', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  Full Team Data ({result.salesData.length} executives)
                </summary>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 10, fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#C8EDD6' }}>
                      {['Name', 'Orders', 'Order Value', 'Quotes', 'Open Quotes'].map(h => (
                        <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 700, color: '#0A3D1F' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {result.salesData.map((e, i) => (
                      <tr key={e.code} style={{ background: i % 2 === 0 ? '#fff' : '#f6fcf8' }}>
                        <td style={{ padding: '6px 10px', fontWeight: 600, color: '#1a2e1a' }}>{e.name}</td>
                        <td style={{ padding: '6px 10px', color: '#333' }}>{e.order_count}</td>
                        <td style={{ padding: '6px 10px', color: '#1B5E3B', fontWeight: 600 }}>₹{Number(e.order_value).toLocaleString('en-IN')}</td>
                        <td style={{ padding: '6px 10px', color: '#333' }}>{e.quote_count}</td>
                        <td style={{ padding: '6px 10px', color: '#333' }}>{e.open_quotes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}
          </div>
        )}

        {/* Standard result */}
        {result && !result.needs_clarification && result.type !== 'analytics' && result.type !== 'smart_dashboard' && result.type !== 'contact' && result.type !== 'recommendation' && (
          <div style={S.results}>

            {/* Summary */}
            <div style={S.summaryBanner}>
              <div style={S.summaryIcon}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1B5E3B" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/>
                </svg>
              </div>
              <span style={{ flex: 1 }}>{result.interpretation?.summary || 'Query processed successfully.'}</span>
              <button onClick={onSave} style={S.saveBtn} className="btn-lift">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
                  <polyline points="17 21 17 13 7 13 7 21"/>
                  <polyline points="7 3 7 8 15 8"/>
                </svg>
                Save
              </button>
            </div>

            {/* Insight */}
            {result.interpretation?.insight && (
              <div style={S.insightBanner}>
                <span>💡</span>
                <span style={{ flex: 1 }}>{result.interpretation.insight}</span>
              </div>
            )}

            {/* Meta chips */}
            <div style={S.metaRow}>
              {result.meta?.intent && (
                <span style={S.metaChip}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/></svg>
                  {result.meta.intent}
                </span>
              )}
              {result.meta?.injected_tables?.length > 0 && (
                <span style={S.metaChip}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/></svg>
                  {result.meta.injected_tables.join(', ')}
                </span>
              )}
              {result.interpretation?.confidence_label === 'high' && (
                <span style={{ ...S.metaChip, background: '#dcfce7', color: '#166534' }}>
                  Verified
                </span>
              )}
              {result.interpretation?.confidence_label === 'low' && (
                <span style={{ ...S.metaChip, background: '#fff7ed', color: '#c2410c' }}>
                  Low accuracy — verify result
                </span>
              )}
              {result.meta?.retry_count > 0 && (
                <span style={{ ...S.metaChip, background: '#fffbeb', color: '#d97706' }}>
                  {result.meta.retry_count} retry
                </span>
              )}
            </div>

            {/* SQL card */}
            <div style={S.sqlCard}>
              <div style={S.sqlHeader}>
                <div style={S.sqlTitle}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#4CAF7D" strokeWidth="2">
                    <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
                  </svg>
                  Generated SQL
                </div>
                <button onClick={copySQL} style={S.copyBtn} className="btn-lift">
                  {sqlCopied ? (
                    <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg> Copied</>
                  ) : (
                    <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg> Copy</>
                  )}
                </button>
              </div>
              <pre style={S.sqlPre}>{result.sql}</pre>
            </div>

            {/* Results table */}
            <div style={S.tableCard}>
              <div style={S.tableHeader}>
                <div style={S.tableTitle}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6b6b6b" strokeWidth="2">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/>
                    <line x1="9" y1="3" x2="9" y2="21"/>
                  </svg>
                  Query Results
                  <span style={S.recordBadge}>{result.rowCount ?? 0} rows</span>
                  {result.executionMs != null && (
                    <span style={S.timeBadge}>{result.executionMs}ms</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={onExportPDF} style={S.exportBtn} disabled={!result.rows?.length} className="btn-lift">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                    PDF
                  </button>
                  <button onClick={onExportExcel} style={S.exportBtn} disabled={!result.rows?.length} className="btn-lift">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Excel
                  </button>
                </div>
              </div>

              {result.rows?.length > 0 ? (
                <div style={S.tableWrap}>
                  {result.ai_renamed?.length > 0 && (
                    <div style={S.aiRenamedBadge}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
                      AI renamed {result.ai_renamed.length} column{result.ai_renamed.length > 1 ? 's' : ''} — review in Admin → Schema → Column Aliases
                    </div>
                  )}
                  <table style={S.table}>
                    <thead>
                      <tr>
                        {Object.keys(result.rows[0]).map(col => (
                          <th key={col} style={S.th}>{result.column_map?.[col] || col}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {result.rows.map((row, i) => (
                        <tr key={i} className="table-row" style={{ background: i % 2 === 0 ? '#fff' : '#f2faf8' }}>
                          {Object.values(row).map((val, j) => {
                            const display = val === null ? null : formatCell(val);
                            const raw = val === null ? '' : String(val);
                            return (
                              <td key={j} style={S.td} title={raw.length > 28 ? raw : undefined}>
                                {val === null ? <span style={S.null}>—</span> : display}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={S.empty}>
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <span>No records found for this query</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Clarification Card ────────────────────────────────────────────────────

function ClarificationCard({ question, options, onSelect }) {
  const lines = question ? question.split('\n').filter(Boolean) : [];
  return (
    <div style={S.clarifyCard}>
      <div style={S.clarifyHeader}>
        <div style={S.clarifyIcon}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/>
          </svg>
        </div>
        <span style={S.clarifyTitle}>Could you clarify?</span>
      </div>
      <div style={S.clarifyBody}>
        {lines.map((line, i) => (
          <p key={i} style={{ margin: '2px 0', lineHeight: 1.6 }}>{line}</p>
        ))}
      </div>
      {options?.length > 0 && (
        <div style={S.clarifyOptions}>
          {options.map((opt) => (
            <button
              key={opt}
              onClick={() => onSelect(opt)}
              className="chip-hover"
              style={S.clarifyChip}
            >
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Follow-up detection ───────────────────────────────────────────────────

// ERP-domain entities that make a query self-contained
const ERP_ENTITIES = /\b(quotation|quote|sales|revenue|customer|stock|inventory|vendor|item|product|order|invoice|credit\s*note|report|analysis|dashboard|monthly|yearly|trend|pending|open|closed|won|lost|cancelled)\b/i;

// Words/patterns that signal a follow-up modifier rather than a standalone query
const FOLLOWUP_STARTERS = /^(of |for |only |with |where |filter |but |also |above |below |between |just |in |not |except |excluding |and |from those|show only|give only|now |limit )/i;

const REFERENTIAL = /\b(those|them\b|the results|the above|previous results?|same filter|that query|that data|those records)\b/i;

function isFollowUpQuery(text, hasHistory) {
  if (!hasHistory) return false;
  const lower = text.toLowerCase().trim();

  // Explicit referential phrases → definitely follow-up
  if (REFERENTIAL.test(lower)) return true;

  // "top N <entity>" is a standalone query even when history exists
  if (/^top \d+ /i.test(lower) && ERP_ENTITIES.test(lower)) return false;

  // Starts with a filter/modifier word AND is short → incomplete without context
  if (FOLLOWUP_STARTERS.test(lower) && text.length < 80) return true;

  // Very short query with no ERP entity → almost certainly a modifier
  if (text.length < 45 && !ERP_ENTITIES.test(lower)) return true;

  return false;
}

// ── Cell formatter ────────────────────────────────────────────────────────

function formatCell(val) {
  if (typeof val === 'number') return val.toLocaleString('en-IN');
  if (val instanceof Date) return val.toLocaleDateString('en-IN');
  if (typeof val === 'string') {
    // SQL Server ISO datetime  e.g. "2026-05-18T14:37:10.000Z"
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(val)) {
      const d = new Date(val);
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
    // Date-only "2026-05-18"
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
      const [y, m, d] = val.split('-');
      return `${d}/${m}/${y}`;
    }
  }
  return String(val);
}

// ── Helpers ───────────────────────────────────────────────────────────────

function confidenceBg(label) {
  if (label === 'high')   return '#ecfdf5';
  if (label === 'medium') return '#fffbeb';
  return '#fef2f2';
}
function confidenceColor(label) {
  if (label === 'high')   return '#059669';
  if (label === 'medium') return '#d97706';
  return '#dc2626';
}

// ── Styles ────────────────────────────────────────────────────────────────

const S = {
  page: {
    display: 'flex', flexDirection: 'column',
    height: '100vh', overflow: 'hidden'
  },

  header: {
    padding: '28px 48px 16px',
    borderBottom: 'none',
    background: '#EAF7F7',
    flexShrink: 0
  },
  title:    { fontSize: 26, fontWeight: 700, letterSpacing: '-0.4px', marginBottom: 4 },
  subtitle: { fontSize: 13, color: '#6b6b6b' },

  tabStrip: {
    display: 'flex', alignItems: 'flex-end', gap: 2,
    padding: '0 48px', paddingTop: 6,
    background: '#EAF7F7', borderBottom: '1px solid #d4ebe0',
    flexShrink: 0, overflowX: 'auto'
  },
  tab: {
    display: 'flex', alignItems: 'center', gap: 5,
    padding: '6px 12px', borderRadius: '8px 8px 0 0',
    cursor: 'pointer', fontSize: 12, fontWeight: 500, color: '#6b6b6b',
    background: 'transparent', border: 'none', borderBottom: '2px solid transparent',
    transition: 'all 0.15s ease', whiteSpace: 'nowrap', userSelect: 'none',
    maxWidth: 180
  },
  tabActive: {
    color: '#1B5E3B', fontWeight: 600,
    background: 'rgba(27,94,59,0.08)', borderBottom: '2px solid #1B5E3B'
  },
  tabName: {
    overflow: 'hidden', textOverflow: 'ellipsis',
    whiteSpace: 'nowrap', maxWidth: 130
  },
  tabClose: {
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#b0b0b0', fontSize: 15, lineHeight: 1,
    padding: '0 1px', borderRadius: 3, flexShrink: 0,
    display: 'flex', alignItems: 'center'
  },
  tabAdd: {
    background: 'none', border: 'none', cursor: 'pointer',
    color: '#4CAF7D', fontSize: 20, lineHeight: 1,
    padding: '4px 8px', borderRadius: 8, flexShrink: 0,
    fontWeight: 300, marginLeft: 4, alignSelf: 'center'
  },

  chatArea: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '24px 48px',
    display: 'flex',
    flexDirection: 'column',
    gap: 32
  },

  // ── Hero welcome state ──
  hero: { paddingBottom: 8, display: 'flex', flexDirection: 'column', gap: 0 },
  heroGreeting: { fontSize: 28, fontWeight: 800, letterSpacing: '-0.5px', marginBottom: 6 },
  heroTagline:  { fontSize: 15, color: '#6b6b6b', marginBottom: 28, fontWeight: 400 },

  heroGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 22, maxWidth: 720 },
  heroCard: {
    background: '#fff', border: '1.5px solid #cce8d8', borderRadius: 16,
    padding: '20px 22px', cursor: 'pointer', textAlign: 'left',
    display: 'flex', flexDirection: 'column', gap: 6, position: 'relative',
    fontFamily: 'Inter, sans-serif',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease'
  },
  heroCardIcon:  { fontSize: 26, lineHeight: 1, marginBottom: 4 },
  heroCardTitle: { fontSize: 14, fontWeight: 700, color: '#1a1a1a' },
  heroCardDesc:  { fontSize: 12, color: '#9b9b9b', lineHeight: 1.5 },
  heroCardArrow: { position: 'absolute', bottom: 16, right: 18, fontSize: 16, color: '#c0bdb8', transition: 'color 0.18s, transform 0.18s' },

  moreRow:  { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 },
  moreLabel: { fontSize: 11, color: '#c0bdb8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginRight: 2 },

  // ── Chips (legacy, still used in moreRow) ──
  chips: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  chip: {
    background: '#fff', border: '1px solid #cce8d8', borderRadius: 20,
    padding: '6px 14px', fontSize: 12, color: '#4b4b4b', cursor: 'pointer',
    fontFamily: 'Inter, sans-serif', boxShadow: '0 1px 3px rgba(0,0,0,0.04)'
  },

  // ── Input bar ──
  inputBar: {
    flexShrink: 0,
    padding: '14px 48px 22px',
    background: '#EAF7F7',
    borderTop: '1px solid #d4ebe0'
  },

  // ── Collapsed pill ──
  pillBar: {
    display: 'flex', alignItems: 'center', gap: 14,
    background: '#fff',
    border: '1.5px solid #cce8d8',
    borderRadius: 50,
    padding: '10px 12px 10px 14px',
    cursor: 'pointer',
    boxShadow: '0 4px 20px rgba(27,94,59,0.10)',
    animation: 'pillPulse 3s ease-in-out infinite',
    userSelect: 'none',
    transition: 'border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease',
  },
  pillIcon: {
    width: 36, height: 36, borderRadius: '50%',
    background: 'linear-gradient(135deg, #E8F6EE, #C8EDD6)',
    border: '1px solid #C8EDD6',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  pillPlaceholder: {
    flex: 1, fontSize: 14.5, color: '#b0b0b0', fontFamily: 'Inter, sans-serif',
  },
  pillSendCircle: {
    width: 40, height: 40, borderRadius: '50%',
    background: 'linear-gradient(135deg, #0A2E17 0%, #1B5E3B 30%, #2E7D52 60%, #1B5E3B 80%, #0A2E17 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    boxShadow: '0 4px 14px rgba(27,94,59,0.40)',
  },
  contextHint: {
    display: 'flex', alignItems: 'center', gap: 6,
    fontSize: 11, color: '#9b9b9b', marginBottom: 8
  },
  clearBtn: {
    marginLeft: 'auto', background: 'rgba(220,38,38,0.06)',
    border: '1px solid rgba(220,38,38,0.2)',
    borderRadius: 6, padding: '3px 10px', fontSize: 11,
    color: '#dc2626', cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 4,
    fontWeight: 500, transition: 'background 0.15s, border-color 0.15s'
  },
  queryBox: {
    background: '#fff', border: '1.5px solid #cce8d8', borderRadius: 14,
    padding: '14px 18px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    transition: 'border-color 0.2s ease, box-shadow 0.2s ease'
  },
  textarea: {
    width: '100%', border: 'none', outline: 'none', resize: 'none',
    fontSize: 15, color: '#1a1a1a', background: 'transparent',
    lineHeight: 1.6, minHeight: 30, maxHeight: 160,
    fontFamily: 'Inter, sans-serif', overflow: 'hidden'
  },
  queryFooter: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 },
  enterHint: { fontSize: 12, color: '#c0bdb8', display: 'flex', alignItems: 'center', gap: 4 },
  kbd: {
    display: 'inline-block', background: '#E8F6EE', border: '1px solid #C8EDD6',
    borderRadius: 4, padding: '1px 5px', fontSize: 11, color: '#1B5E3B', fontFamily: 'inherit'
  },
  analyticsBtn: {
    display: 'flex', alignItems: 'center', gap: 7,
    background: '#fff', border: '1.5px solid #cce8d8',
    color: '#4b4b4b', borderRadius: 9,
    padding: '9px 16px', fontSize: 13, fontWeight: 600,
  },
  sendBtn: {
    display: 'flex', alignItems: 'center', gap: 7,
    background: 'linear-gradient(135deg, #0A2E17 0%, #1B5E3B 28%, #2E7D52 52%, #1B5E3B 76%, #0A2E17 100%)',
    color: '#fff', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 9,
    padding: '9px 20px', fontSize: 13, fontWeight: 600,
    boxShadow: '0 4px 16px rgba(27,94,59,0.42), inset 0 1px 0 rgba(255,255,255,0.12)'
  },
  spinner: {
    width: 14, height: 14, borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.35)', borderTopColor: '#fff',
    display: 'inline-block', animation: 'spin 0.7s linear infinite'
  },

  // ── Chat turn ──
  turn: { display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 },

  userBubbleRow: { display: 'flex', justifyContent: 'flex-end' },
  userBubble: {
    maxWidth: '70%',
    background: 'linear-gradient(135deg, #0A2E17 0%, #1B5E3B 30%, #2E7D52 55%, #1B5E3B 80%, #0A2E17 100%)',
    borderRadius: '14px 14px 4px 14px', padding: '12px 16px',
    boxShadow: '0 4px 16px rgba(27,94,59,0.3), inset 0 1px 0 rgba(255,255,255,0.1)'
  },
  userBubbleLabel: { fontSize: 10, color: 'rgba(255,255,255,0.65)', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' },
  userBubbleText: { fontSize: 14, color: '#fff', lineHeight: 1.5 },

  responseArea: { paddingLeft: 0 },

  // ── Loading ──
  loadingBox: { display: 'flex', alignItems: 'center', gap: 20, padding: '16px 0' },
  loadingRing: {
    position: 'relative', width: 50, height: 50,
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
  },
  ring1: {
    position: 'absolute', inset: 0, borderRadius: '50%',
    border: '2px solid rgba(27,94,59,0.15)',
    borderTopColor: '#1B5E3B', animation: 'spin 1s linear infinite'
  },
  ring2: {
    position: 'absolute', inset: 6, borderRadius: '50%',
    border: '2px solid rgba(46,125,82,0.15)',
    borderBottomColor: '#2E7D52', animation: 'spin 1.5s linear infinite reverse'
  },
  loadingTitle: { fontSize: 14, fontWeight: 600, color: '#1a1a1a', marginBottom: 6 },
  loadingSteps: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  loadingStep: {
    fontSize: 11, color: '#9b9b9b', background: '#E8F6EE',
    padding: '2px 8px', borderRadius: 10, border: '1px solid #C8EDD6'
  },

  // ── Error ──
  errorBox: {
    display: 'flex', alignItems: 'center', gap: 8,
    background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10,
    padding: '12px 16px', color: '#dc2626', fontSize: 13
  },

  // ── Result ──
  results: { display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 },

  summaryBanner: {
    display: 'flex', alignItems: 'flex-start', gap: 10,
    background: 'linear-gradient(135deg, #E8F6EE, #f0fdf4)',
    border: '1px solid #C8EDD6', borderRadius: 12,
    padding: '13px 16px', fontSize: 13, color: '#4b4b4b',
    boxShadow: '0 2px 8px rgba(27,94,59,0.08)'
  },
  summaryIcon: {
    width: 28, height: 28, borderRadius: 8,
    background: '#E0F5EE', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
  },
  saveBtn: {
    display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0,
    background: 'none', border: '1px solid #81C784', borderRadius: 7,
    padding: '5px 12px', fontSize: 12, color: '#1B5E3B', cursor: 'pointer'
  },
  insightBanner: {
    display: 'flex', alignItems: 'flex-start', gap: 10,
    background: '#f0fdf4', border: '1px solid #bbf7d0',
    borderRadius: 12, padding: '13px 16px', fontSize: 13, color: '#4b4b4b'
  },
  lowConf: { color: '#d97706', marginLeft: 4, fontSize: 11, flexShrink: 0 },
  metaRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  metaChip: {
    display: 'flex', alignItems: 'center', gap: 5,
    background: '#E8F6EE', color: '#0A3D1F', fontSize: 11,
    padding: '4px 10px', borderRadius: 20, border: '1px solid #C8EDD6', fontWeight: 500
  },
  sqlCard: {
    background: '#071A0E', borderRadius: 12, overflow: 'hidden',
    border: '1px solid #1A4A2E', boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
  },
  sqlHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 16px', borderBottom: '1px solid #1A4A2E',
    background: 'rgba(255,255,255,0.03)'
  },
  sqlTitle: { display: 'flex', alignItems: 'center', gap: 7, color: '#4CAF7D', fontSize: 12, fontWeight: 600 },
  copyBtn: {
    display: 'flex', alignItems: 'center', gap: 5,
    background: 'none', border: '1px solid #1A4A2E',
    borderRadius: 6, padding: '4px 10px', color: '#9ca3af', fontSize: 12, cursor: 'pointer'
  },
  sqlPre: {
    padding: '16px', margin: 0, overflowX: 'auto', fontSize: 12.5,
    lineHeight: 1.75, color: '#e2e8f0', fontFamily: 'JetBrains Mono, monospace',
    whiteSpace: 'pre-wrap', wordBreak: 'break-word'
  },
  tableCard: {
    background: '#fff', border: '1px solid #cce8d8',
    borderRadius: 12, overflow: 'hidden',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
  },
  tableHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 16px', borderBottom: '1px solid #e4f5ed', background: '#f2faf8'
  },
  tableTitle: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#4b4b4b', fontWeight: 600 },
  recordBadge: {
    background: '#E0F5EE', color: '#1B5E3B', fontSize: 11,
    padding: '2px 9px', borderRadius: 12, fontWeight: 600
  },
  timeBadge: {
    background: '#e4f5ed', color: '#6b6b6b', fontSize: 11,
    padding: '2px 9px', borderRadius: 12
  },
  exportBtn: {
    display: 'flex', alignItems: 'center', gap: 5,
    background: 'linear-gradient(135deg, #0A2E17 0%, #1B5E3B 30%, #2E7D52 55%, #1B5E3B 80%, #0A2E17 100%)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 7, padding: '6px 12px', color: '#fff', fontSize: 12, cursor: 'pointer',
    boxShadow: '0 3px 10px rgba(27,94,59,0.38), inset 0 1px 0 rgba(255,255,255,0.12)'
  },
  aiRenamedBadge: {
    display: 'flex', alignItems: 'center', gap: 6,
    background: '#f0fdf4', borderBottom: '1px solid #bbf7d0',
    padding: '7px 14px', fontSize: 11.5, color: '#15803d'
  },
  tableWrap: { overflowX: 'auto', maxHeight: 440, overflowY: 'auto', width: '100%' },
  table: { borderCollapse: 'collapse', fontSize: 13, width: '100%' },
  th: {
    textAlign: 'left', padding: '10px 14px', fontWeight: 600,
    color: '#4b4b4b', background: '#E8F6EE', borderBottom: '1px solid #E0F5EE',
    whiteSpace: 'nowrap', fontSize: 12, position: 'sticky', top: 0, letterSpacing: '0.01em',
    maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis'
  },
  td: {
    padding: '9px 14px', color: '#1a1a1a', borderBottom: '1px solid #EAF7F7',
    whiteSpace: 'nowrap', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis'
  },
  null: { color: '#d1d5db', fontStyle: 'italic', fontSize: 12 },
  empty: {
    padding: '48px 16px', textAlign: 'center', color: '#9b9b9b',
    fontSize: 13, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10
  },

  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
    backdropFilter: 'blur(4px)'
  },
  dialog: {
    background: '#fff', borderRadius: 16, padding: '28px',
    width: 400, boxShadow: '0 24px 60px rgba(0,0,0,0.2)'
  },
  dialogTitle: { fontSize: 17, fontWeight: 700, marginBottom: 6, color: '#1a1a1a' },
  dialogInput: {
    width: '100%', border: '1.5px solid #cce8d8', borderRadius: 9,
    padding: '11px 14px', fontSize: 14, outline: 'none',
    fontFamily: 'Inter, sans-serif', marginBottom: 18
  },
  dialogActions: { display: 'flex', gap: 8, justifyContent: 'flex-end' },
  cancelBtn: {
    background: 'none', border: '1px solid #cce8d8', borderRadius: 8,
    padding: '9px 18px', fontSize: 13, cursor: 'pointer', color: '#6b6b6b'
  },
  saveDialogBtn: {
    background: 'linear-gradient(135deg, #0A2E17 0%, #1B5E3B 28%, #2E7D52 52%, #1B5E3B 76%, #0A2E17 100%)',
    color: '#fff', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8,
    padding: '9px 20px', fontSize: 13, cursor: 'pointer', fontWeight: 600,
    boxShadow: '0 4px 16px rgba(27,94,59,0.42), inset 0 1px 0 rgba(255,255,255,0.12)'
  },

  // ── Chat message card (greeting / irrelevant) ──
  chatMsgCard: {
    display: 'flex', alignItems: 'flex-start', gap: 12,
    background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)',
    border: '1px solid #bae6fd', borderRadius: 12,
    padding: '16px 18px',
    boxShadow: '0 2px 8px rgba(14,165,233,0.08)'
  },
  chatMsgIcon: { fontSize: 22, flexShrink: 0, lineHeight: 1 },
  chatMsgText: { fontSize: 13.5, color: '#0c4a6e', flex: 1 },

  // ── Clarification card ──
  clarifyCard: {
    background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
    border: '1px solid #fde68a', borderRadius: 12,
    padding: '16px 18px',
    boxShadow: '0 2px 8px rgba(217,119,6,0.08)'
  },
  clarifyHeader: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 },
  clarifyIcon: {
    width: 28, height: 28, borderRadius: 8,
    background: '#fef3c7', border: '1px solid #fde68a',
    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
  },
  clarifyTitle: { fontSize: 13, fontWeight: 700, color: '#92400e' },
  clarifyBody: { fontSize: 13, color: '#78350f', marginBottom: 14, lineHeight: 1.6 },
  clarifyOptions: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  clarifyChip: {
    background: '#fff', border: '1px solid #fcd34d', borderRadius: 20,
    padding: '7px 16px', fontSize: 12.5, color: '#92400e', cursor: 'pointer',
    fontFamily: 'Inter, sans-serif', fontWeight: 500,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)'
  }
};
